import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import type { Tables } from "@/integrations/supabase/types";
import { formatPublicLocation } from "@/lib/locationPrivacy";
import {
  AppButton,
  AppButtonRow,
  AppCard,
  AppCardBody,
  AppEmptyState,
  AppLoadingState,
  AppSection,
  AppSectionHeader,
  AppText,
  kindredFontFamilies,
} from "../../ui-foundation";
import { orderEventsForMember } from "../../shared/activities";
import { buildLocationCommunityHub } from "../../shared/location";

type HubMember = Pick<Tables<"profiles">, "id" | "display_name" | "avatar_url" | "postcode" | "country_code">;
type HubEvent = Pick<
  Tables<"events">,
  "id" | "title" | "location" | "event_date" | "description" | "is_free" | "cost" | "is_published" | "submitted_by" | "created_at"
>;

const formatEventDate = (value: string) =>
  new Date(value).toLocaleDateString("en-GB", {
    weekday: "short",
    day: "numeric",
    month: "short",
  });

const CommunityLocalHub = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [postcode, setPostcode] = useState<string | null>(null);
  const [countryCode, setCountryCode] = useState<string | null>(null);
  const [members, setMembers] = useState<HubMember[]>([]);
  const [events, setEvents] = useState<HubEvent[]>([]);

  useEffect(() => {
    let active = true;

    const load = async () => {
      if (!user?.id) {
        if (!active) return;
        setLoading(false);
        return;
      }

      setLoading(true);

      const [{ data: profile }, { data: nextMembers }, { data: nextEvents }] = await Promise.all([
        supabase.from("my_private_profile").select("postcode, country_code").maybeSingle(),
        supabase
          .from("profiles")
          .select("id, display_name, avatar_url, postcode, country_code")
          .neq("id", user.id)
          .limit(60),
        supabase
          .from("events")
          .select("id, title, location, event_date, description, is_free, cost, is_published, submitted_by, created_at")
          .eq("is_published", true)
          .order("event_date", { ascending: true }),
      ]);

      if (!active) return;

      const nextPostcode = profile?.postcode ?? null;
      setPostcode(nextPostcode);
      setCountryCode(profile?.country_code ?? null);
      setMembers((nextMembers ?? []) as HubMember[]);
      setEvents(
        orderEventsForMember(
          (nextEvents ?? []) as HubEvent[],
          { postcode: nextPostcode, countryCode: profile?.country_code ?? null },
          user.id,
        ),
      );
      setLoading(false);
    };

    void load();

    return () => {
      active = false;
    };
  }, [user?.id]);

  const hub = useMemo(
    () =>
        buildLocationCommunityHub({
          postcode,
          countryCode,
          members,
          events,
        }),
    [countryCode, events, members, postcode],
  );

  if (loading) {
    return <AppLoadingState title="Loading your local hub" body="Gathering nearby members and local plans." />;
  }

  if (!hub.hasHub) {
    return (
      <AppSection pt="$xs" pb="$sm">
        <AppEmptyState
          title="Set up your local hub"
          body="Add your postcode, ZIP code, or local postal code in settings and Kindred will gather nearby members and local events here."
          action={
            <AppButton tone="outline" size="sm" onPress={() => navigate("/settings")}>
              Open settings
            </AppButton>
          }
        />
      </AppSection>
    );
  }

  return (
    <AppSection pt="$xs" pb="$sm" gap="$sm">
      <AppCard>
        <AppCardBody gap="$sm">
          <AppSectionHeader
            title={hub.label}
            subtitle="A calmer first local space for the people and plans closest to home."
          />
          <AppText color="$colorMuted" fontSize="$sm" lineHeight="$md">
            {hub.totalMembers} nearby member{hub.totalMembers === 1 ? "" : "s"} · {hub.totalEvents} local event
            {hub.totalEvents === 1 ? "" : "s"}
          </AppText>
          <AppButtonRow>
            <AppButton tone="outline" size="sm" onPress={() => navigate("/community/members")}>
              Find members
            </AppButton>
            <AppButton tone="outline" size="sm" onPress={() => navigate("/activities?section=whats-on")}>
              Open What's On
            </AppButton>
          </AppButtonRow>
        </AppCardBody>
      </AppCard>

      <AppSection gap="$xs">
        <AppSectionHeader
          title="Nearby members"
          subtitle="A first gentle view of single-parent families around your area."
        />
        {hub.members.length ? (
          hub.members.map((member) => (
            <AppCard key={member.id}>
              <AppCardBody flexDirection="row" alignItems="center" gap="$sm">
                <button
                  type="button"
                  onClick={() => navigate(`/community/member/${member.id}`)}
                  className="flex min-w-0 flex-1 items-center gap-3 text-left"
                >
                  <Avatar className="h-12 w-12">
                    <AvatarImage src={member.avatar_url ?? undefined} />
                    <AvatarFallback>{(member.display_name ?? "K")[0]?.toUpperCase()}</AvatarFallback>
                  </Avatar>
                  <div className="min-w-0 flex-1">
                    <AppText fontFamily={kindredFontFamilies.sansBold} fontSize="$sm" lineHeight="$sm">
                      {member.display_name ?? "Member"}
                    </AppText>
                    <AppText color="$colorMuted" fontSize="$xs" lineHeight="$sm">
                      {formatPublicLocation(member.postcode, member.country_code)}
                    </AppText>
                  </div>
                </button>
                <AppButton tone="outline" size="xs" onPress={() => navigate(`/community/member/${member.id}`)}>
                  View
                </AppButton>
              </AppCardBody>
            </AppCard>
          ))
        ) : (
          <AppEmptyState
            title={`No members showing near ${hub.outwardCode} yet`}
            body="As more single-parent families join nearby, this local hub will start to feel fuller."
          />
        )}
      </AppSection>

      <AppSection gap="$xs">
        <AppSectionHeader
          title="What's happening nearby"
          subtitle="Local plans that already look relevant to your area."
        />
        {hub.events.length ? (
          hub.events.map((event) => (
            <AppCard key={event.id}>
              <AppCardBody flexDirection="row" alignItems="center" gap="$sm">
                <button
                  type="button"
                  onClick={() => navigate(`/whats-on/${event.id}`)}
                  className="flex min-w-0 flex-1 text-left"
                >
                  <div className="min-w-0 flex-1">
                    <AppText fontFamily={kindredFontFamilies.sansBold} fontSize="$sm" lineHeight="$sm">
                      {event.title}
                    </AppText>
                    <AppText color="$colorMuted" fontSize="$xs" lineHeight="$sm">
                      {formatEventDate(event.event_date)} · {event.location?.trim() || "Location to be confirmed"}
                    </AppText>
                  </div>
                </button>
                <AppButton tone="outline" size="xs" onPress={() => navigate(`/whats-on/${event.id}`)}>
                  Open
                </AppButton>
              </AppCardBody>
            </AppCard>
          ))
        ) : (
          <AppEmptyState
            title={`No local events picked up for ${hub.outwardCode} yet`}
            body="We'll show nearby event ideas here as more local tags and local listings come through."
          />
        )}
      </AppSection>
    </AppSection>
  );
};

export default CommunityLocalHub;
